import { useEffect, useId, useRef } from 'react'
import type { ReactNode, RefObject } from 'react'
import { createPortal } from 'react-dom'
import { CloseOutlined } from '../icons'

export interface ModalProps {
  readonly open: boolean
  readonly onClose: () => void
  /** The dialog's accessible name. Always required, even when `headerContent`
   * replaces the visible `<h2>` — when `headerContent` is set, this string is
   * used as `aria-label` on the dialog instead of `aria-labelledby`, so
   * screen readers still announce a real name (never an unlabelled dialog). */
  readonly title: string
  readonly children: ReactNode
  readonly footer?: ReactNode
  /** 470/480/560/820px variants seen in the source (components.md "Modal"). */
  readonly maxWidth?: number
  /** Optional replacement for the default `<h2>{title}</h2>` header bar —
   * for callers whose header row is richer than a plain heading (e.g. a
   * thumbnail + name + secondary line, `EditConfigurationModal`). Renders in
   * place of the `<h2>`, still inside `.modal-header` alongside the close
   * button. `title` is still required (see its own doc) and drives the
   * dialog's accessible name in this case. */
  readonly headerContent?: ReactNode
  /** Appended to the dialog's own `modal` class (never replaces it) — for a
   * caller that needs a modal-specific visual override (e.g.
   * `EditConfigurationModal`'s deeper `0 16px 48px` shadow, UID-225) without
   * changing the shared `--shadow-modal` token every OTHER modal in the app
   * also renders with. */
  readonly className?: string
  /** UID-263 (ADR-0087 D4, UX spec §3 focus-management) — OPTIONAL override
   * for where focus lands on open, in place of the default (the dialog
   * container itself). For a consequential/data-mutating confirm (e.g. the
   * bulk-add-to-cart confirm modal), the caller can point this at its
   * least-destructive control (typically "Cancel") so a stray Enter can't
   * accidentally confirm. Omitted → unchanged default behavior — every
   * existing caller is unaffected. */
  readonly initialFocusRef?: RefObject<HTMLElement | null>
  /** UID-410 AC5 — omit the `.modal-header` bar (title + close button)
   * entirely, for a state whose body already draws its own centered
   * heading (e.g. `GenerateQuoteModal`'s "Quote generated" ready state,
   * `Fictiv Platform.dc.html:5011-5017`, which has no header bar at all).
   * `title` is still required and still drives the dialog's accessible
   * name via `aria-label` — only the VISIBLE bar is suppressed. The
   * buyer can still dismiss via Escape or the backdrop; callers that hide
   * the header must give the body its own explicit way forward (e.g. a
   * footer "Done"/"Continue" action). Defaults to `false` — every
   * existing caller is unaffected. */
  readonly hideHeader?: boolean
  /** UID-430 (Generate-quote confirm modal design-fidelity fix) — optional
   * subtitle line rendered directly under the default `<h2>` title (design:
   * `Fictiv Platform.dc.html:4967`, 13px/`--fg-2`/margin-top:3px). Ignored
   * when `headerContent` replaces the title block, since that slot owns its
   * own full header layout. Omitted → no subtitle renders and the header
   * looks exactly as before — every existing caller is unaffected. */
  readonly subtitle?: ReactNode
  /** UID-460 — `'center'` (default) is the existing scale-in dialog, used
   * unchanged by every caller as of this prop's introduction. `'drawer-
   * right'` anchors the panel to the viewport's right edge, full height,
   * sliding in from the right — for a surface whose OWN content is
   * genuinely a linear list a shopper works through top-to-bottom (e.g.
   * `SeriesConfiguratorDrawer`'s linear field list), matching the reference
   * design's slide-in drawer chrome rather than a centered dialog. Every
   * other behavior (focus-trap, Escape, backdrop-click, portal-to-body) is
   * shell-agnostic and unchanged by this prop — only backdrop alignment and
   * the panel's own position/size/entrance animation differ. */
  readonly variant?: 'center' | 'drawer-right'
}

/** UID-263 (ADR-0087 D4) — the focus-return fallback for a modal whose
 * trigger is no longer in the DOM on close (see the doc comment at the
 * call site). Focuses the page's main `<h1>`, giving it a `tabindex="-1"`
 * first if it doesn't already have one (a bare `<h1>` isn't natively
 * focusable) so screen-reader/keyboard users land somewhere meaningful
 * instead of focus silently falling back to `<body>`. A no-op if the page
 * has no `<h1>` at all — a rare shape this repo's pages don't have today. */
function focusFallbackHeading(): void {
  const heading = document.querySelector<HTMLElement>('h1')
  if (!heading) return
  if (!heading.hasAttribute('tabindex')) {
    heading.setAttribute('tabindex', '-1')
  }
  heading.focus()
}

/** Every node type that can appear in the tab order at all — the coarse
 * FIRST pass of the focus-trap query, narrowed by {@link focusableNodes}. */
const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

/**
 * The dialog's GENUINELY tabbable nodes, in DOM order — matching
 * {@link FOCUSABLE_SELECTOR} is necessary but NOT sufficient.
 *
 * UID-460 code-review W2 (pre-existing primitive bug, fixed here for every
 * caller at once): the raw `querySelectorAll` result includes nodes the
 * browser will never move focus to, and the trap's "first"/"last" pair is
 * computed from that list. A caller whose LAST matching node is `disabled`
 * (e.g. `SeriesConfiguratorDrawer`'s "Add to Cart", disabled from the moment
 * the drawer opens; also `NewRfqModal`) therefore got a trap that both
 * FAILED to wrap forward (Tab from the real last control fell through to
 * `<body>`, escaping the dialog) and DEAD-ENDED backward (Shift+Tab from the
 * first control called `.focus()` on a disabled node — a silent no-op).
 *
 * Excluded: natively `disabled` controls, `tabindex="-1"` (programmatic
 * focus only — the base selector already excludes an explicit `tabindex`
 * attribute of `-1`, this also catches e.g. `<button tabindex="-1">`),
 * hidden inputs, and anything inside a `hidden`/`aria-hidden="true"`
 * subtree or explicitly `display:none`/`visibility:hidden`. `aria-disabled`
 * is deliberately NOT excluded — such a control is intentionally still
 * focusable so assistive tech can announce why it's unavailable.
 */
function focusableNodes(root: HTMLElement): readonly HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((node) => {
    if (node.hasAttribute('disabled') || node.tabIndex < 0) return false
    if (node instanceof HTMLInputElement && node.type === 'hidden') return false
    if (node.closest('[hidden], [aria-hidden="true"]')) return false
    const style = window.getComputedStyle(node)
    return style.display !== 'none' && style.visibility !== 'hidden'
  })
}

/**
 * Modal/drawer shell (components.md "Modal" — slide/scale-in entrance,
 * backdrop-click-to-close, header+body+footer regions). Traps focus while
 * open and restores it to the triggering element on close, per
 * components.md's explicit a11y requirement. `Escape` closes.
 *
 * Shared by both "modal" and "drawer" surfaces per interactions.md §6 /
 * open-questions.md #10 (the Configurator is spec'd as one component usable
 * in either shell) — pass `variant="drawer-right"` for a slide-from-right
 * treatment instead of the default center scale-in (UID-460); the
 * focus-trap/escape/backdrop behavior here is shell-agnostic either way.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = 480,
  headerContent,
  className,
  initialFocusRef,
  hideHeader = false,
  subtitle,
  variant = 'center',
}: ModalProps) {
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)

  // UID-289 (Gate-5 follow-up) — focus-on-open + focus-restore-on-close is
  // deliberately SEPARATE from the keydown effect below and keyed ONLY on
  // `[open, initialFocusRef]`, never `onClose`. A consumer that wraps its
  // `onClose` in extra logic (reset-then-close) hands a new function identity
  // on every render; if this focus effect depended on `onClose`, each such
  // re-render would tear it down + re-run it — re-focusing the dialog
  // container and stealing focus from a field the user is typing in (the
  // NewRfqModal search box hit exactly this). Splitting the two concerns fixes
  // it at the primitive so no consumer needs to memoize `onClose` defensively.
  useEffect(() => {
    if (!open) return
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null
    ;(initialFocusRef?.current ?? dialogRef.current)?.focus()
    return () => {
      // UID-263 (ADR-0087 D4, UX spec §3 focus-return edge case) — a
      // trigger that unmounts while this modal is open (e.g. the bulk-add
      // confirm's "Add to cart" empties the RFQ draft, unmounting the
      // summary rail's bulk button) leaves `previouslyFocusedRef` pointing
      // at a now-detached node; calling `.focus()` on it is a silent no-op
      // that strands focus nowhere. Guard for that and fall back to the
      // page's main heading, matching the UX spec's recommendation, rather
      // than throwing or leaving focus unset. Every OTHER caller's trigger
      // still exists on close, so this fallback branch never runs for them
      // — unchanged behavior.
      const previous = previouslyFocusedRef.current
      if (previous && document.body.contains(previous)) {
        previous.focus()
      } else {
        focusFallbackHeading()
      }
    }
  }, [open, initialFocusRef])

  // Escape-to-close + Tab focus-trap. Keyed on `[open, onClose]` so Escape
  // always calls the latest `onClose`; re-subscribing when that identity
  // changes only swaps the document listener and has NO focus side effect
  // (that is the whole point of keeping it out of the focus effect above).
  useEffect(() => {
    if (!open) return
    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = focusableNodes(dialogRef.current)
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, onClose])

  if (!open) return null

  // The backdrop's click-to-dismiss is a mouse convenience layer, not a
  // keyboard-operable control — Escape (handled above via a document
  // keydown listener) is the keyboard equivalent, per components.md
  // "Modal"'s close behavior. Checking `target === currentTarget` (rather
  // than giving the dialog its own onClick + stopPropagation) means only
  // the backdrop itself needs a click handler at all — the dialog never
  // gets one, so it never needs a keyboard-event counterpart either. The
  // dialog itself deliberately hand-rolls the ARIA `role="dialog"` pattern
  // (rather than native <dialog>) so this shares one focus-trap/Escape/
  // backdrop implementation across both "modal" and "drawer" shells
  // (open-questions.md #10).
  //
  // PORTALED to `document.body` (UID-131 follow-up defect fix) rather than
  // rendered in place: a fixed-position `inset:0` overlay is only GUARANTEED
  // to cover the entire viewport — including the sticky top bar — when it is
  // a direct child of <body>, immune to whatever stacking context any
  // ancestor between here and <body> happens to introduce. Before this fix
  // every caller rendered the backdrop deep inside `<main>` (a sibling of the
  // sticky top bar, not an ancestor of it), which is exactly the shape that
  // let the dimmed backdrop stop at `<main>`'s edge instead of covering the
  // top bar. The full-viewport overlay (`--z-modal-backdrop` = 150, well
  // above the top bar's `--z-sticky` = 30) also makes the rest of the page
  // non-interactive to pointer users — it physically covers every other
  // element — and the focus trap above keeps keyboard focus inside the
  // dialog, so the top bar is unreachable by mouse OR keyboard while open.
  const variantClass = variant === 'drawer-right' ? 'modal-drawer-right' : undefined
  const modalClassName = [variantClass, className].filter(Boolean).join(' ')

  return createPortal(
    // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation above>
    <div
      className={
        variant === 'drawer-right' ? 'modal-backdrop modal-backdrop-drawer-right' : 'modal-backdrop'
      }
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        // biome-ignore lint/a11y/useSemanticElements: <explanation above>
        role="dialog"
        aria-modal="true"
        aria-labelledby={headerContent || hideHeader ? undefined : titleId}
        aria-label={headerContent || hideHeader ? title : undefined}
        tabIndex={-1}
        className={modalClassName ? `modal ${modalClassName}` : 'modal'}
        style={{ maxWidth }}
      >
        {!hideHeader && (
          <div className="modal-header">
            {headerContent ?? (
              <div className="modal-title-group">
                <h2 id={titleId} className="modal-title">
                  {title}
                </h2>
                {subtitle && <p className="modal-subtitle">{subtitle}</p>}
              </div>
            )}
            <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>
              <CloseOutlined aria-hidden="true" />
            </button>
          </div>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}
